<template>
  <BaseModule
    v-bind="$props"
    class="container hotel-promo-list"
    data-module="hotel-promo-list"
    :style="{
      '--img-width': imgWidth + 'px',
      '--img-height': imgHeightByRatio + '%',
      '--card-bg-color': cardBackgroundColor,
      '--card-text-color': cardTextColor,
      '--see-more-button-color': seeMoreButtonColor,
    }"
  >
    <Warning
      v-if="editing"
      text="O módulo não possui um hotel selecionado"
    >
    </Warning>

    <Loader v-else-if="loading" />

    <div
      v-else
      class="content"
    >
      <header
        v-if="title || text"
        class="header base-title-fontsize"
      >
        <component
          :is="titleTag"
          v-if="title"
          class="tit tit-g"
        >
          {{ title }}
        </component>
        <div
          v-if="text"
          class="txt"
          v-html="text"
        ></div>
      </header>

      <div
        ref="swiper"
        class="list-wrapper swiper"
      >
        <ul
          v-if="showPromos"
          class="promo-list swiper-wrapper"
          :class="{
            disabled: !isSliderEnabled,
          }"
        >
          <li
            v-for="promo in visiblePromoList"
            :key="promo.code"
            :data-promo="promo.code"
            class="promo-list__item swiper-slide"
          >
            <div class="promo-card">
              <div class="aspect-ratio-parent">
                <div class="aspect-ratio-child">
                  <LImage
                    v-if="promo.image"
                    class="absolute-full"
                    :src="promo.image"
                    :alt="promo.name"
                  />
                </div>
              </div>
              <p
                v-if="promo.discount"
                class="discount"
              >
                {{ $tc('discount', promo.discount) }}
              </p>

              <div class="infos">
                <h4 class="name">
                  {{ promo.name }}
                </h4>
                <div
                  v-if="showDescription"
                  class="promo-description"
                  :class="{
                    'promo-description--empty': !hasText(promo.description),
                    clamped: promo.seeMoreEnabled,
                    'see-more': promo.seeMore,
                  }"
                >
                  <p
                    :ref="'description-text-' + promo.code"
                    class="promo-description__text"
                  >
                    {{ promo.description }}
                  </p>
                </div>

                <button
                  v-if="showDescription"
                  class="toggle-see-more"
                  :class="{
                    'toggle-see-more--hidden': !promo.seeMoreEnabled,
                    open: promo.seeMore,
                  }"
                  type="button"
                  @click="toggleSeeMore(promo)"
                >
                  <span
                    v-if="!promo.seeMore"
                    class="more"
                    >{{ $t('readMore') }}</span
                  >
                  <span
                    v-else
                    class="less"
                    >{{ $t('readLess') }}</span
                  >
                </button>
                <div
                  v-if="promo.priceWithDiscount && showValor"
                  class="value"
                >
                  <div
                    v-if="promo.discount"
                    class="value__label small-info"
                  >
                    <span>{{ $t('from') }}</span>
                    <span class="slashed">
                      {{ valueFormat(promo.priceFull) }}
                    </span>
                  </div>
                  <div
                    v-else
                    class="small-info"
                  >
                    {{ $t('priceText') }}
                  </div>
                  <div
                    class="value__info"
                    :style="{ color: getValueColor(promo) }"
                  >
                    <span v-if="promo.discount">{{ $t('for') }}</span>
                    <span v-html="getValueHTML(promo.priceWithDiscount)"></span>
                  </div>
                </div>

                <EditorLink
                  :model="promo.linkUrl"
                  class="cta"
                  :class="{
                    vazado: ctaAltStyle,
                  }"
                  @click.native="trackCustomEvent(promo)"
                >
                  {{ buttonText }}
                </EditorLink>
              </div>
            </div>

            <div
              v-if="promo.policies"
              class="disclaimer"
              @mouseover="showTooltip"
              @mouseleave="hideTooltip"
            >
              <span class="slb-tooltip arrow-right-down">
                <ul class="items-tooltip">
                  <li
                    v-for="(policy, index) in promo.policies"
                    :key="index"
                  >
                    {{ policy }}
                  </li>
                </ul>
              </span>
              <IconInfoTooltip />
            </div>
          </li>
        </ul>

        <!-- If we need navigation buttons -->
        <div
          v-show="isSliderEnabled && showPromos"
          :class="`swiper-button-prev btn-prev-${instanceId}`"
        ></div>
        <div
          v-show="isSliderEnabled && showPromos"
          :class="`swiper-button-next btn-next-${instanceId}`"
        ></div>

        <EmptyState
          v-if="isEmptyState"
          :title="$t('notAvailableTitle')"
          :subtitle="$t('notAvailableSubtitle')"
          :img="promoImg"
        />
      </div>

      <button
        v-show="hasPromosToLoad && !isSliderEnabled"
        class="cta vazado cta--ver-mais"
        @click="showMorePromos()"
      >
        {{ $t('seeMore') }}
      </button>
    </div>
  </BaseModule>
</template>

<script>
import { mapGetters, mapState } from 'vuex';

import base from '@/mixins/base';
import getApiHotels from '@/mixins/getApiHotels';
import formatCurrency from '@/helpers/formatCurrency';
import Loader from '@/components/Loader.vue';
import Warning from '@/components/Warning.vue';
import { pushDataLayer } from '@/helpers/gtm';
import handlePromos from '@/helpers/handlePromos';
import randomizeArray from '@/helpers/randomizeArray';
import IconInfoTooltip from '@/components/icons/IconInfoTooltip.vue';
import IconFilter from '@/components/icons/IconFilter.vue';
import Swiper from 'swiper';
import { Navigation } from 'swiper/modules';
import BaseModule from './generic/BaseModule.vue';
import EditorLink from './EditorLink.vue';
import EmptyState from './EmptyState.vue';

const emptyPromoUrlPath = require('@/assets/promoOptin.png');

export default {
  name: 'PromoHotel',
  components: {
    BaseModule,
    Loader,
    Warning,
    EditorLink,
    EmptyState,
    IconInfoTooltip,
    IconFilter,
  },
  mixins: [getApiHotels, base],
  inheritAttrs: false,
  props: {
    ...BaseModule.props,
    text: {
      type: String,
      default: '',
    },
    hotel: {
      type: Number,
      default: null,
    },
    customPromos: {
      type: Array,
      default() {
        return [];
      },
    },
    showValor: {
      type: Boolean,
      default: true,
    },
    showDescription: {
      type: Boolean,
      default: true,
    },
    orderBy: {
      type: String,
      default: 'initial-date',
    },
    isSliderEnabled: {
      type: Boolean,
      default: false,
    },
    buttonText: {
      type: String,
      default: 'Saiba mais',
    },
    cardBackgroundColor: {
      type: String,
      default: '#fbfbfb',
    },
    cardTextColor: {
      type: String,
      default: '#474747',
    },
    ctaAltStyle: {
      type: Boolean,
      default: true,
    },
    seeMoreButtonColor: {
      type: String,
      default: '#575757',
    },
    imgWidth: {
      type: Number,
      default: 360,
    },
    imgHeight: {
      type: Number,
      default: 270,
    },
  },
  data() {
    return {
      loading: false,
      promos: [],
      cleanPromos: [],
      cardsPerRow: 3,
      visiblePromosNumber: 6,
      promoImg: emptyPromoUrlPath,
      isMobile: window.innerWidth < 640,
      swiper: null,
    };
  },
  computed: {
    hasPromos() {
      return this.cleanPromos.length > 0;
    },
    visiblePromoList() {
      return this.isSliderEnabled
        ? this.cleanPromos
        : this.cleanPromos.slice(0, this.visiblePromosNumber);
    },
    showPromos() {
      return !this.loading && this.hasPromos;
    },
    hasPromosToLoad() {
      return this.visiblePromosNumber < this.cleanPromos.length;
    },
    isEmptyState() {
      return !this.hasPromos && !this.loading && !this.editing;
    },
    editing() {
      return typeof this.$parent === 'undefined' && !this.hotel;
    },
    apiLocale() {
      switch (this.$i18n.locale) {
        case 'en':
          return 'en-us';
        case 'es':
          return 'es-es';
        default:
          return 'pt-br';
      }
    },
    imgHeightByRatio() {
      // altura da imagem é calculada através de padding-top, assim as proporções da imagem são respeitadas quando a largura não cabe na janela
      return Number(((100 / this.imgWidth) * this.imgHeight).toFixed(2));
    },
    ...mapState(['apiData']),
    ...mapGetters(['settings']),
  },
  watch: {
    hotel(val) {
      if (typeof val === 'number') {
        this.requestPromos();
        this.$store.commit('SET_ACTIVE_EDITABLE_OBJECT_DATA', {
          path: 'data.customPromos',
          value: [],
        });
      }
    },
    visiblePromoList() {
      this.manageSeeMore();
      this.swiperReset();
    },
    customPromos: {
      handler() {
        this.setCleanPromos();
      },
      deep: true,
    },
    orderBy() {
      this.setCleanPromos();
    },
    loading() {
      this.swiperReset();
    },
    isSliderEnabled() {
      this.swiperReset();
    },
  },
  mounted() {
    window.addEventListener('resize', () => {
      this.isMobile = window.innerWidth < 640;
    });
    this.getApiHotels();

    if (this.hotel) {
      this.requestPromos();
    }

    this.cardsPerRow = this.rowCardCalc(window.innerWidth);
    this.visiblePromosNumber = this.cardsPerRow * 2;
  },
  methods: {
    swiperInit() {
      this.$nextTick(() => {
        this.swiper = new Swiper(this.$refs.swiper, {
          modules: [Navigation],
          observer: true,
          spaceBetween: 32,
          slidesPerView: 1.1,
          allowTouchMove: false,
          breakpoints: {
            576: {
              slidesPerView: 'auto',
            },
          },
          navigation: {
            nextEl: `.btn-next-${this.instanceId}`,
            prevEl: `.btn-prev-${this.instanceId}`,
          },
        });
      });
    },
    swiperDestroy() {
      this.swiper.destroy();
      this.swiper = null;
    },
    swiperReset() {
      if (this.swiper) {
        this.swiperDestroy();
      }

      if (this.isSliderEnabled && this.visiblePromoList.length > 1) {
        this.swiperInit();
      }
    },
    getValueColor(promo) {
      return promo.discount
        ? this.settings.promotionColor
        : this.settings.primaryColor;
    },
    async requestPromos() {
      this.loading = true;
      const platform = this.isMobile ? 'mobile' : 'desktop';
      const url = `${this.settings.letsbookApiId}/promocoes/${this.hotel}/${platform}/${this.apiLocale}`;

      if (!this.apiData[url]) {
        try {
          await this.$store.dispatch('loadApiData', url);
        } catch {
          this.loading = false;
          return;
        }
      }

      this.promos = handlePromos(this.apiData[url]);
      this.loading = false;
      this.setCleanPromos();
    },
    valueFormat(price) {
      return formatCurrency(price, 'BRL');
    },
    getValueHTML(value) {
      const formattedValue = this.valueFormat(value);
      const rgx = /(\D*)(\d{1,3}(?:[.,\s]\d{3})*)((?:[.,\s]\d{2})?\D*)/; // (any)(INT)(any)
      return formattedValue.replace(rgx, '$1<span class="integer">$2</span>$3'); // $1 might be empty
    },
    hasText(text) {
      return typeof text === 'string' && /\S/.test(text);
    },
    manageSeeMore() {
      // precisa do nextTick ou ele não encontra os elementos na primeira inicialização
      this.$nextTick(() => {
        this.cleanPromos.forEach((promo) => {
          const el = this.$refs[`description-text-${promo.code}`];

          if (el && el[0]?.offsetHeight > 17 * 3) {
            // eslint-disable-next-line no-param-reassign
            promo.seeMoreEnabled = true;
          }
        });
      });
    },
    toggleSeeMore(promo) {
      // eslint-disable-next-line no-param-reassign
      promo.seeMore = !promo.seeMore;
    },
    showTooltip(event) {
      const el = event.currentTarget;
      const slbTooltip = el.querySelector('.slb-tooltip');
      const position = el.getBoundingClientRect();
      if (position.top < slbTooltip.clientHeight + 10) {
        slbTooltip.classList.add('open-down');
      } else {
        slbTooltip.classList.add('open-up');
      }
    },
    hideTooltip(event) {
      const el = event.currentTarget;
      const slbTooltip = el.querySelector('.slb-tooltip');
      slbTooltip.classList.remove('open-up', 'open-down');
    },
    rowCardCalc(innerWidth) {
      const containerWidth = Math.min(1200, innerWidth - 2 * 20);
      const flexGap = 24;
      const cardsPerRow = Math.floor(
        (containerWidth + flexGap) / (this.imgWidth + flexGap),
      );
      return Math.max(1, cardsPerRow);
    },
    showMorePromos() {
      this.visiblePromosNumber += this.cardsPerRow * 2;
    },
    setCleanPromos() {
      const newList = this.promos.map((promo) => {
        // procura no customPromos pelo id para usar as informações customizadas se disponíveis
        const customData = this.customPromos.find(
          (custom) => custom.promoId === promo.Codigo,
        );

        return {
          code: promo.Codigo,
          name: promo.Nome,
          image:
            customData?.image ||
            promo.Imagem?.Url ||
            'https://s3-sa-east-1.amazonaws.com/letslandingpage/assets/images/indisponivel.gif',
          description: promo.Descricao,
          policies: [
            promo.ItensDescricao.TipoPagamento,
            promo.ItensDescricao.TipoPagamento.Reembolsavel,
            promo.ItensDescricao.Pensao,
            promo.ItensDescricao.MinimoNoites,
          ],
          priceFull: promo.Tarifa.ValorSemDesconto,
          priceWithDiscount: promo.Tarifa.Valor,
          discount: promo.Tarifa.ExibirDesconto
            ? promo.Tarifa.PercentualDesconto
            : 0,
          seeMoreEnabled: false,
          seeMore: false,
          promoStart: promo.PeriodoVigente.Inicio,
          linkUrl:
            customData?.linkUrl.type !== 'defaultLink' &&
            customData?.linkUrl.value
              ? customData.linkUrl
              : promo.UrlPromocao,
          hotel: promo.Hotel,
          hotelId: promo.CodigoHotel,
        };
      });
      this.cleanPromos = this.orderList(newList);
    },
    orderList(promos) {
      const promoList = [...promos];

      switch (this.orderBy) {
        case 'initial-date':
          return promoList.sort((promoA, promoB) => {
            const date1 = promoA.promoStart;
            const date2 = promoB.promoStart;

            if (!date1) {
              return 1;
            }
            if (!date2) {
              return -1;
            }

            return new Date(date1) - new Date(date2);
          });
        case 'lowest-price':
          return promoList.sort(
            (promoA, promoB) =>
              promoA.priceWithDiscount - promoB.priceWithDiscount,
          );
        case 'highest-price':
          return promoList.sort(
            (promoA, promoB) =>
              promoB.priceWithDiscount - promoA.priceWithDiscount,
          );
        case 'highest-discount':
          return promoList.sort((promoA, promoB) => {
            const discount1 = promoA.discount ? promoA.discount : 0;
            const discount2 = promoB.discount ? promoB.discount : 0;

            return discount2 - discount1;
          });
        case 'random':
          return randomizeArray(promoList);
        default:
          return promoList;
      }
    },
    trackCustomEvent(promo) {
      pushDataLayer({
        event: 'select_hotel_promotion',
        hotel_id: String(promo.hotelId),
        hotel_name: promo.hotel,
        promotion_name: promo.name,
        price_starting: promo.priceWithDiscount,
      });
    },
  },
  metadata: {
    extends: BaseModule.metadata,
    tabs: [
      {
        name: 'content',
        groups: [
          {
            name: 'config',
            position: 0,
            label: 'Configurações',
            fields: [
              {
                name: 'hotel',
                type: 'VueSelect',
                label: 'Listar promoções',
                helpText:
                  'Selecione um hotel para listar as promoções<br><span class="warning">Atenção: você perderá seus "itens editados" se trocar o hotel</span>',
                options() {
                  const hotelsToOptions = (hotels) =>
                    hotels.map((hotel) => ({
                      label: hotel.Nome,
                      value: hotel.Codigo,
                    }));

                  // Are hotels already loaded?
                  if (this.hotels.length > 0) {
                    return hotelsToOptions(this.hotels);
                  }

                  // THIS IS PURE MAGIC
                  return new Promise((resolve) => {
                    const unwatch = this.$watch('hotels', (hotels) => {
                      resolve(hotelsToOptions(hotels));
                      unwatch();
                    });
                  });
                },
              },
              {
                name: 'showDescription',
                type: 'Checkbox',
                label: 'Mostrar descrições da tarifa',
              },
              {
                name: 'showValor',
                type: 'Checkbox',
                label: 'Mostrar preços',
              },
              {
                name: 'orderBy',
                type: 'VueSelect',
                label: 'Ordenar promoções por:',
                options: [
                  {
                    label: 'Data de início',
                    value: 'initial-date',
                  },
                  {
                    label: 'Menor preço',
                    value: 'lowest-price',
                  },
                  {
                    label: 'Maior preço',
                    value: 'highest-price',
                  },
                  {
                    label: 'Maior desconto',
                    value: 'highest-discount',
                  },
                  {
                    label: 'Ordem aleatória',
                    value: 'random',
                  },
                ],
              },
              {
                name: 'isSliderEnabled',
                type: 'Checkbox',
                label: 'Ativar deslizamento dos itens',
                helpText: 'Ativa o deslizamento dos itens como carrossel.',
              },
              {
                name: 'ctaAltStyle',
                type: 'Checkbox',
                label: 'Botões estilo "vazado"',
              },
            ],
          },
          {
            name: 'text',
            fields: [
              {
                name: 'title',
                maxlength: 40,
              },
              {
                name: 'text',
                type: 'Html',
                label: 'Texto',
              },
              {
                name: 'buttonText',
                type: 'ShortText',
                maxlength: 24,
                label: 'Texto dos botões',
              },
            ],
            toRemove: ['subtitle'],
          },
          {
            name: 'customPromos',
            label: 'Editar itens',
            helpText:
              'Por padrão, usamos as informações registradas no Motor de Reservas, mas aqui você pode editar as imagens e links de cada item de promoção.',
            fields: [
              {
                name: 'customPromos',
                type: 'List',
                itemLabel({ promoId }) {
                  if (!promoId) {
                    return '<span style="color:red">Promoção não selecionada</span>';
                  }

                  if (this.loading) {
                    return `Promoção ID ${promoId}`;
                  }

                  const thisExp = this.promos.find(
                    (promo) => promo.Codigo === promoId,
                  );

                  return thisExp
                    ? thisExp.Nome
                    : '<span style="color:red">Promoção selecionada não encontrada</span>';
                },
                addItemLabel: 'Editar item de promoção',
                isSortable: false,
                fields: [
                  {
                    name: 'promoId',
                    type: 'VueSelect',
                    label: 'Selecionar promoção',
                    options() {
                      const promoToOptions = (promos) =>
                        promos.map((promo) => ({
                          label: promo.Nome,
                          value: promo.Codigo,
                        }));

                      // Are promos already loaded?
                      if (this.promos.length > 0) {
                        return promoToOptions(this.promos);
                      }

                      // THIS IS PURE MAGIC
                      return new Promise((resolve) => {
                        const unwatch = this.$watch('promos', (promos) => {
                          resolve(promoToOptions(promos));
                          unwatch();
                        });
                      });
                    },
                  },
                  {
                    name: 'image',
                    type: 'ImageFile',
                    label: 'Imagem',
                  },
                  {
                    name: 'linkUrl',
                    type: 'Url',
                    availableTypes: ['externalLink', 'page', 'defaultLink'],
                    default: {
                      type: 'defaultLink',
                      value: '',
                    },
                  },
                ],
              },
            ],
          },
        ],
      },
      {
        name: 'appearance',
        groups: [
          {
            name: 'colors',
            label: 'Cores',
            fields: [
              {
                name: 'cardBackgroundColor',
                type: 'ColorPicker',
                label: 'Cor de fundo dos itens',
              },
              {
                name: 'cardTextColor',
                type: 'ColorPicker',
                label: 'Cor do texto dos itens',
              },
              {
                name: 'seeMoreButtonColor',
                type: 'ColorPicker',
                label: 'Cor do botão "Ver mais promoções"',
              },
            ],
          },
          {
            name: 'imageConfig',
            position: 0,
            label: 'Dimensões das imagens',
            fields: [
              {
                name: 'imgWidth',
                label: 'Largura das imagens',
                type: 'Range',
                min: 240,
                max: 600,
                step: 1,
                format: {
                  suffix: 'px',
                },
              },
              {
                name: 'imgHeight',
                label: 'Altura das imagens',
                type: 'Range',
                min: 240,
                max: 600,
                step: 1,
                format: {
                  suffix: 'px',
                },
              },
            ],
          },
        ],
      },
    ],
  },
  i18n: {
    messages: {
      pt: {
        discount: '{count}% OFF',
        from: 'de ',
        for: 'por ',
        priceText: 'a partir de',
        unit: 'unidade',
        seeMore: 'Ver mais promoções',
        readMore: 'Ler mais',
        readLess: 'Recolher',
        notAvailableTitle: 'Ops, não encontramos nenhuma promoção disponível.',
        notAvailableSubtitle:
          'Em breve listaremos aqui todas as nossas promoções para você escolher a que mais se encaixa no seu perfil.',
      },
      en: {
        discount: '{count}% OFF',
        from: 'from ',
        for: 'for ',
        priceText: 'prices from',
        unit: 'unit',
        seeMore: 'See more promos',
        readMore: 'Read more',
        readLess: 'Less',
        notAvailableTitle: 'Ops, we did not find any available item.',
        notAvailableSubtitle:
          "We'll soon list all our offers here so that you can find the one that best suits your profile.",
      },
      es: {
        discount: '{count}% OFF',
        from: 'de ',
        for: 'por ',
        priceText: 'a partir de',
        unit: 'unidad',
        seeMore: 'Ver más promociones',
        readMore: 'Ler más',
        readLess: 'Menos',
        notAvailableTitle: 'Ops, no encontramos promociones disponibles.',
        notAvailableSubtitle:
          'En breve enumeraremos aquí todos nuestras promociones para que elijas el que mejor se adapta a tu perfil.',
      },
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/styles/base/mixins.scss';

.container {
  padding-top: var(--padding-top);
  padding-bottom: var(--padding-bottom);
  overflow: hidden;

  @include media-breakpoint-down(sm) {
    padding-left: 8vw;
    padding-right: 8vw;
    padding-top: var(--padding-top-mobile);
    padding-bottom: var(--padding-bottom-mobile);
  }
}

.content {
  max-width: 1200px !important;
}

.hotel-promo-list {
  text-align: center;
  max-width: 100%;

  .list-wrapper {
    display: flex;
    align-items: flex-start;

    .promo-list {
      &.disabled {
        display: flex;
        flex-direction: column;
        gap: 32px;
        width: 100%;

        @include media-breakpoint-up(sm) {
          flex-direction: row;
          flex-wrap: wrap;
          flex-grow: 1;
          justify-content: center;
          gap: 24px;
        }
      }

      &__item {
        position: relative;
        max-width: 100%;

        .disclaimer {
          text-align: right;
          position: absolute;
          top: 0;
          right: 0;
          padding: 10px;
          cursor: pointer;

          .slb-tooltip {
            visibility: hidden;
            opacity: 0;
            color: $gray-70;
            left: unset;
            right: 0;
            z-index: 99;

            &.open-up,
            &.open-down {
              opacity: 1;
              visibility: visible;
              transform: translateY(0);
            }

            .items-tooltip {
              line-height: 1.8em;
              text-align: left;
            }
          }
        }

        .promo-card {
          display: flex;
          flex-direction: column;
          background-color: var(--card-bg-color, $white);
          color: var(--card-text-color, $gray-900);
          width: var(--img-width);
          max-width: 100%;
          height: auto;
          position: relative;
          border-radius: 12px;

          @include media-breakpoint-down(sm) {
            width: 100%;
          }

          .aspect-ratio-parent {
            width: 100%;

            .aspect-ratio-child {
              width: 100%;
              padding-top: var(--img-height);
              position: relative;
            }

            .img {
              border-radius: 12px;
            }
          }

          .infos {
            padding: 24px;
            text-align: left;
            display: flex;
            flex-direction: column;
            align-items: flex-start;
          }

          .discount {
            color: $lb-white;
            background-color: var(--promo-color);
            padding: 8px 16px;
            line-height: 1;
            position: absolute;
            top: -15px;
            left: 24px;
            z-index: 50;
            font-weight: $lb-medium;
            letter-spacing: -0.005em;
            margin-bottom: 0;
            border-radius: 8px;
            z-index: 10;
          }

          .name {
            margin: 0;
            font-size: $size-normal;
            font-weight: $lb-bold;
            height: 2.4em;
            line-height: 1.2;
            display: -webkit-box;
            -webkit-line-clamp: 2;
            -webkit-box-orient: vertical;
            overflow: hidden;
            text-overflow: ellipsis;
          }

          .promo-description {
            overflow: hidden;
            text-overflow: ellipsis;
            display: -webkit-box;
            -webkit-box-orient: vertical;
            font-size: $size-small;
            text-align: left;
            margin-top: 18px;
            height: calc(3 * 17px);
            max-height: calc(3 * 17px);
            transition: max-height 1s ease-in-out;

            &.clamped {
              -webkit-line-clamp: 3;
            }

            &.see-more {
              -webkit-line-clamp: unset !important;
              height: auto;
              max-height: 1000px;
            }

            &__text {
              line-height: 17px;
            }

            &--empty {
              height: calc(3 * 21px);
            }
          }

          .toggle-see-more {
            position: relative;
            border: 0;
            background: none;
            font-weight: $lb-bold;
            outline: inherit;
            padding: 4px 0;
            margin-bottom: 8px;
            cursor: pointer;
            color: var(--primary-color);

            &--hidden {
              visibility: hidden;
              opacity: 0;
            }

            &::after {
              content: '';
              margin-left: 0.625vw;
              display: inline-block;
              border-top: 7px solid;
              border-left: 5px solid transparent !important;
              border-right: 5px solid transparent !important;
              position: absolute;
              top: 50%;
              transform: translateY(-50%) rotate(0);
              transition: transform 0.4s ease-in-out;
            }

            &.open {
              &::after {
                transform: translateY(-50%) rotate(180deg);
              }
            }
          }

          .value {
            margin-bottom: 16px;

            &__label {
              .slashed {
                text-decoration: line-through;
              }
            }

            &__info {
              font-weight: $lb-bold;
              font-size: $size-smaller;

              ::v-deep .integer {
                line-height: 1;
                font-size: 20px;
              }
            }
          }

          .small-info {
            font-size: $size-smaller;
            font-weight: 400;
          }

          .cta {
            width: auto;
            min-width: 150px;
            max-width: 100%;
            padding: 16px 8px;
            font-weight: $lb-bold;
            white-space: normal;
            text-decoration: none;
            overflow-wrap: anywhere;
            border-radius: 12px;
            transition: 0.2s;

            @include media-breakpoint-down(sm) {
              width: 100%;
            }
          }
        }
      }
    }
  }

  .swiper {
    overflow: unset;

    .swiper-wrapper {
      .swiper-slide {
        width: auto;
      }

      &.disabled {
        .swiper-slide {
          margin-right: 0 !important;
        }
      }
    }

    .swiper-button-prev,
    .swiper-button-next {
      width: 40px;
      top: 40%;

      @include media-breakpoint-up(sm) {
        width: 60px;
      }

      &::after {
        background-color: rgba($lb-black, 0.333);
        color: $lb-white;
        border-radius: 50%;
        display: flex;
        justify-content: center;
        align-items: center;
        width: 40px;
        height: 40px;
        font-size: 20px;

        @include media-breakpoint-up(sm) {
          width: 50px;
          height: 50px;
          font-size: 30px;
        }
      }
    }

    @include media-breakpoint-down(sm) {
      .swiper-button-prev {
        left: -3%;
      }
      .swiper-button-next {
        right: -3%;
      }
    }
  }

  .cta {
    overflow: hidden;

    &.cta--ver-mais {
      color: var(--see-more-button-color);
      border-color: var(--see-more-button-color);
      margin: 32px auto 0;
      border-radius: 12px;
      overflow: hidden;
    }
  }
}
</style>
